import { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  Card,
  CardContent,
  Link,
} from '@mui/material'
import { Security } from '@mui/icons-material'
import { authApi } from '@api/endpoints/auth'
import { useAuthStore } from '@store/authStore'
import { portalAccessDenial } from '../portalAccess'

export default function MfaVerifyPage() {
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [useBackupCode, setUseBackupCode] = useState(false)
  const { mfaToken, login, mfaRequired } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    // Redirect if no MFA token
    if (!mfaRequired || !mfaToken) {
      navigate('/login')
    }
  }, [mfaRequired, mfaToken, navigate])

  const handleCodeChange = (value: string) => {
    if (useBackupCode) {
      // Backup codes are alphanumeric and often printed with a dash, so keep letters, digits and
      // dashes; the server does the real validation.
      setCode(value.replace(/[^A-Za-z0-9-]/g, '').slice(0, 20))
      return
    }

    // Authenticator codes: digits only, 6 long
    const numericValue = value.replace(/\D/g, '').slice(0, 6)
    setCode(numericValue)

    // Auto-submit when 6 digits entered (also covers the OS one-time-code autofill)
    if (numericValue.length === 6) {
      handleSubmit(numericValue)
    }
  }

  const handleSubmit = async (codeValue?: string) => {
    const verificationCode = codeValue || code

    if (!verificationCode || !mfaToken) {
      setError('Please enter a verification code')
      return
    }

    setError(null)
    setIsLoading(true)

    try {
      const response = await authApi.verifyMfa({
        mfaToken,
        code: verificationCode,
      })

      if (response.success && response.token && response.user && portalAccessDenial(response)) {
        // Signed in, but not into the portal: say why here instead of bouncing to the main site.
        setError(portalAccessDenial(response))
      } else if (response.success && response.token && response.user) {
        // MFA verification successful - same token handling as a plain password login
        login(
          response.token,
          response.refreshToken || '',
          response.user,
          response.expiresAt || '',
          response.refreshTokenExpiresAt || '',
        )
        // Same order as LoginPage: a pending share code wins, then the page they were heading to
        // (LoginPage forwards its router state here).
        const pendingShareCode = localStorage.getItem('pendingShareCode')
        if (pendingShareCode) {
          localStorage.removeItem('pendingShareCode')
          navigate(`/code/${pendingShareCode}`, { replace: true })
          return
        }
        const from = (location.state as { from?: { pathname?: string; search?: string; hash?: string } } | null)?.from
        const target = from ? `${from.pathname ?? '/'}${from.search ?? ''}${from.hash ?? ''}` : '/'
        navigate(target, { replace: true })
      } else {
        setError(response.message || 'Verification failed')
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Invalid verification code. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Card sx={{ maxWidth: 450, width: '100%' }}>
        <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Security sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h5" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
              Two-Factor Authentication
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {useBackupCode
                ? 'Enter a backup code from your recovery codes'
                : 'Enter the 6-digit code from your authenticator app'}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
            <TextField
              value={code}
              onChange={(e) => handleCodeChange(e.target.value)}
              label={useBackupCode ? 'Backup Code' : 'Verification Code'}
              fullWidth
              margin="normal"
              autoFocus
              autoComplete={useBackupCode ? 'off' : 'one-time-code'}
              inputProps={
                useBackupCode
                  ? {
                      style: { textAlign: 'center', fontSize: '20px', letterSpacing: '2px' },
                      maxLength: 20,
                      inputMode: 'text',
                      autoCapitalize: 'characters',
                      autoCorrect: 'off',
                      spellCheck: false,
                    }
                  : {
                      style: { textAlign: 'center', fontSize: '24px', letterSpacing: '8px' },
                      maxLength: 6,
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                    }
              }
              disabled={isLoading}
            />

            {!useBackupCode && (
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={isLoading || code.length !== 6}
                sx={{ mt: 2, mb: 2 }}
              >
                {isLoading ? 'Verifying...' : 'Verify'}
              </Button>
            )}

            {useBackupCode && (
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={isLoading || code.length === 0}
                sx={{ mt: 2, mb: 2 }}
              >
                {isLoading ? 'Verifying...' : 'Verify Backup Code'}
              </Button>
            )}

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => {
                  setUseBackupCode(!useBackupCode)
                  setCode('')
                  setError(null)
                }}
                sx={{ cursor: 'pointer' }}
              >
                {useBackupCode ? 'Use authenticator code instead' : 'Use backup code instead'}
              </Link>
            </Box>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => navigate('/login', { state: location.state })}
                sx={{ cursor: 'pointer' }}
              >
                Back to login
              </Link>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  )
}
